import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Menu, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import * as voiceLib from 'entities/voice/lib';

import { LocalizedText } from 'shared/api';
import { LANGUAGE_OPTIONS, getLanguageName } from 'shared/config';
import { ConfirmDialog } from 'shared/ui';

const MAX_TEXT_LENGTH = 1000;

type Props = {
  tabLanguages: string[];
  bio: LocalizedText[];
  specialty: LocalizedText[];
  activeLanguage: string;
  missingBioLanguages: string[];
  showErrors: boolean;
  showSummary: boolean;
  onActiveLanguageChange: (lang: string) => void;
  onChange: (next: { tabLanguages: string[]; bio: LocalizedText[]; specialty: LocalizedText[] }) => void;
  onBlur: () => void;
  bioInputRef?: React.RefObject<HTMLInputElement>;
};

/**
 * Языки, на которые переведено описание автора. К полю languages[] (языки самого
 * автора) отношения не имеет и не меняет его: у автора из Индии languages может
 * быть ['hi'], а описание существовать только на en и ru.
 */
export const LanguageContent = ({
  tabLanguages,
  bio,
  specialty,
  activeLanguage,
  missingBioLanguages,
  showErrors,
  showSummary,
  onActiveLanguageChange,
  onChange,
  onBlur,
  bioInputRef,
}: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  const handleAddLanguage = (code: string) => {
    setMenuAnchor(null);
    onChange({ tabLanguages: [...tabLanguages, code], bio, specialty });
    onActiveLanguageChange(code);
  };

  const removeLanguage = (code: string) => {
    const nextTabs = tabLanguages.filter((lang) => lang !== code);
    onChange({
      tabLanguages: nextTabs,
      bio: voiceLib.removeLocalizedLanguage(bio, code),
      specialty: voiceLib.removeLocalizedLanguage(specialty, code),
    });
    if (activeLanguage === code) {
      onActiveLanguageChange(nextTabs[0] ?? 'en');
    }
    setPendingRemoval(null);
  };

  const handleRemoveClick = (event: React.MouseEvent, code: string) => {
    event.stopPropagation();
    const hasText =
      !!voiceLib.getLocalizedText(bio, code).trim() || !!voiceLib.getLocalizedText(specialty, code).trim();
    if (hasText) {
      setPendingRemoval(code);
      return;
    }
    removeLanguage(code);
  };

  const activeName = getLanguageName(activeLanguage);
  const activeBio = voiceLib.getLocalizedText(bio, activeLanguage);
  const isActiveMissing = showErrors && missingBioLanguages.includes(activeLanguage);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Description languages
      </Typography>

      <Box display="flex" alignItems="center" gap={1} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeLanguage}
          onChange={(_, value: string) => onActiveLanguageChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1, minHeight: 40 }}
        >
          {tabLanguages.map((code) => {
            const isMissing = showErrors && missingBioLanguages.includes(code);
            return (
              <Tab
                key={code}
                value={code}
                component="div"
                sx={{ minHeight: 40, textTransform: 'none' }}
                aria-label={isMissing ? `${getLanguageName(code)} — bio is missing` : getLanguageName(code)}
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <span>{code.toUpperCase()}</span>
                    {isMissing && (
                      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main' }} />
                    )}
                    {code !== 'en' && (
                      <IconButton
                        size="small"
                        aria-label={`Remove ${getLanguageName(code)}`}
                        sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
                        onClick={(event) => handleRemoveClick(event, code)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          disabled={tabLanguages.length >= LANGUAGE_OPTIONS.length}
        >
          Add language
        </Button>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {LANGUAGE_OPTIONS.map((option) => (
          <MenuItem
            key={option.code}
            disabled={tabLanguages.includes(option.code)}
            onClick={() => handleAddLanguage(option.code)}
          >
            {option.name} ({option.nativeName})
          </MenuItem>
        ))}
      </Menu>

      {showSummary && missingBioLanguages.length > 0 && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Bio is required for: {voiceLib.formatMissingBioLanguages(missingBioLanguages)}
        </Alert>
      )}

      <TextField
        fullWidth
        label={`Specialty (${activeName})`}
        value={voiceLib.getLocalizedText(specialty, activeLanguage)}
        inputProps={{ maxLength: MAX_TEXT_LENGTH }}
        onChange={(event) =>
          onChange({
            tabLanguages,
            bio,
            specialty: voiceLib.setLocalizedText(specialty, activeLanguage, event.target.value),
          })
        }
        onBlur={onBlur}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        multiline
        rows={3}
        label={`Bio (${activeName}) *`}
        value={activeBio}
        inputRef={bioInputRef}
        inputProps={{ maxLength: MAX_TEXT_LENGTH }}
        onChange={(event) =>
          onChange({
            tabLanguages,
            bio: voiceLib.setLocalizedText(bio, activeLanguage, event.target.value),
            specialty,
          })
        }
        onBlur={onBlur}
        error={isActiveMissing}
        helperText={isActiveMissing ? 'Required field' : `${activeBio.length}/${MAX_TEXT_LENGTH}`}
      />

      <ConfirmDialog
        open={!!pendingRemoval}
        title="Remove language"
        message={`Remove ${
          pendingRemoval ? getLanguageName(pendingRemoval) : ''
        }? Its bio and specialty will be deleted.`}
        onConfirm={() => pendingRemoval && removeLanguage(pendingRemoval)}
        onCancel={() => setPendingRemoval(null)}
      />
    </Box>
  );
};
